import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError, setAccessToken } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = unknown/checking
  const [ready, setReady] = useState(false);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      // If /auth/me fails, the stored token is stale — the api interceptor
      // already clears it on 401, but we mirror the state locally.
      setAccessToken(null);
      setUser(false);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    // Returning from Emergent Google OAuth? Skip the /auth/me probe — the
    // GoogleCallback view will exchange the one-time session_id for a real
    // JWT and then setUser directly. Running fetchMe here would race the
    // exchange and bounce the user back to /login.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setReady(true);
      return;
    }
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // Persist the bearer token so every follow-up request carries it, even
      // if the httpOnly cookie is blocked by a proxy or browser policy.
      if (data?.access_token) setAccessToken(data.access_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  // Emergent Google sign-in — the frontend callback hands us the one-time
  // session_id, we trade it for our own existing JWT via the backend. Only
  // succeeds if the Google email already matches a user the head coach
  // invited; otherwise backend returns 403 with a human-readable message.
  const loginWithGoogleSession = async (sessionId) => {
    try {
      const { data } = await api.post("/auth/google-session", { session_id: sessionId });
      if (data?.access_token) setAccessToken(data.access_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {
      /* ignore */
    }
    setAccessToken(null);
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, ready, login, loginWithGoogleSession, logout, setUser, refresh: fetchMe }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
